/**
 * 仪表板页面
 * 显示用户统计信息、最近收藏和快捷操作
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout, Card, Row, Col, Statistic, List, Tag, Button,
  Typography, Space, Empty, Spin, Avatar, message, Tabs
} from 'antd'
import {
  FileTextOutlined, TagOutlined, FolderOutlined, ClockCircleOutlined,
  SearchOutlined, PlusOutlined, SettingOutlined, LogoutOutlined,
  ReadOutlined, StarOutlined, EyeOutlined, CalendarOutlined,
  ToolOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../store/authStore'
import { api } from '../services/api'
import AddBookmarkModal from '../components/AddBookmarkModal'
import AddToolModal from '../components/AddToolModal'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
import './Dashboard.css'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const { Header, Content } = Layout
const { Title, Text } = Typography

interface Stats {
  article_count: number
  tag_count: number
  folder_count: number
  tool_count: number
  total_reading_time: number
  unread_reading_time: number  // 未读文章阅读时间
  today_collected_count: number  // 今日收藏数
  favorite_count: number
  archived_count: number
}

interface Article {
  id: number
  title: string
  url: string
  summary: string
  site_name: string
  reading_time: number
  collected_at: string
  is_read: boolean
  is_featured: boolean
  last_read_at: string | null
  tags: Array<{ id: number; name: string; color: string }>
}

interface ToolItem {
  id: number
  name: string
  description: string
  source_article_id?: number
  source_system: string
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [stats, setStats] = useState<Stats | null>(null)
  const [recentArticles, setRecentArticles] = useState<Article[]>([])
  const [recentTools, setRecentTools] = useState<ToolItem[]>([])
  const [historyArticles, setHistoryArticles] = useState<Article[]>([])
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [addBookmarkVisible, setAddBookmarkVisible] = useState(false)
  const [addToolVisible, setAddToolVisible] = useState(false)
  const [activeTab, setActiveTab] = useState<'recent' | 'history' | 'featured'>('recent')
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [featuredLoaded, setFeaturedLoaded] = useState(false)

  const handleAddBookmark = () => {
    setAddBookmarkVisible(true)
  }

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // 加载统计信息
      const statsResponse = await api.stats.overview()
      console.log('统计数据响应:', statsResponse)
      if (statsResponse.success) {
        console.log('设置统计数据:', statsResponse.data)
        setStats(statsResponse.data)
      } else {
        console.error('统计数据加载失败:', statsResponse)
      }

      // 加载最近收藏的文章
      const recentResponse = await api.articles.list({
        page: 1,
        page_size: 10
      })

      if (recentResponse.success) {
        setRecentArticles(recentResponse.data.items || [])
      }

      // 加载最近添加的工具
      const toolsResponse = await api.tools.list({ page: 1, size: 10 })
      if (toolsResponse.success) {
        setRecentTools(((toolsResponse.data || {}).items || []))
      }

    } catch (error: any) {
      console.error('加载仪表板数据失败:', error)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const loadHistoryArticles = async (forceReload = false) => {
    if (historyLoaded && !forceReload) return

    try {
      // 加载历史查看的文章（按最后阅读时间排序）
      const historyResponse = await api.articles.history({
        page: 1,
        page_size: 10
      }) as any

      if (historyResponse.success) {
        setHistoryArticles(historyResponse.data.items || [])
        setHistoryLoaded(true)
      }
    } catch (error: any) {
      console.error('加载历史查看失败:', error)
      message.error('加载历史查看失败')
    }
  }

  const loadFeaturedArticles = async (forceReload = false) => {
    if (featuredLoaded && !forceReload) return

    try {
      // 加载精选文章（按最后阅读时间排序）
      const featuredResponse = await api.articles.list({
        page: 1,
        page_size: 10,
        is_featured: true,
        sort_by: 'last_read_at',
        order: 'desc'
      }) as any

      if (featuredResponse.success) {
        setFeaturedArticles(featuredResponse.data.items || [])
        setFeaturedLoaded(true)
      }
    } catch (error: any) {
      console.error('加载精选文章失败:', error)
      message.error('加载精选文章失败')
    }
  }

  const reloadRecentArticles = async () => {
    try {
      const recentResponse = await api.articles.list({
        page: 1,
        page_size: 10
      }) as any

      if (recentResponse.success) {
        setRecentArticles(recentResponse.data.items || [])
      }
    } catch (error: any) {
      console.error('加载最近收藏失败:', error)
      message.error('加载最近收藏失败')
    }
  }

  const handleLogout = () => {
    logout()
  }

  const renderArticleList = (articles: Article[], emptyText: string) => (
    articles.length === 0 ? (
      <Empty
        description={emptyText}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddBookmarkVisible(true)}
        >
          添加收藏
        </Button>
      </Empty>
    ) : (
      <List
        dataSource={articles}
        renderItem={(article) => (
          <List.Item
            className="article-item"
            onClick={() => navigate(`/articles/${article.id}`)}
            actions={[
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={async (e) => {
                  e.stopPropagation()
                  // 标记为已读
                  try {
                    await api.articles.batchSetRead([article.id])
                    setRecentArticles(prev =>
                      prev.map(a =>
                        a.id === article.id ? { ...a, is_read: true } : a
                      )
                    )
                    setHistoryArticles(prev =>
                      prev.map(a =>
                        a.id === article.id ? { ...a, is_read: true } : a
                      )
                    )
                  } catch (error) {
                    console.error('标记为已读失败:', error)
                  }
                  window.open(article.url, '_blank')
                }}
                style={{
                  background: 'var(--success)',
                  border: 'none',
                  borderRadius: '16px',
                  fontWeight: 500,
                  marginRight: 8
                }}
              >
                原文
              </Button>,
              <Button
                type="default"
                size="small"
                icon={<ReadOutlined />}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/articles/${article.id}`)
                }}
                style={{
                  borderRadius: '16px'
                }}
              >
                阅读
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  icon={<FileTextOutlined />}
                  style={{ backgroundColor: 'var(--info)' }}
                />
              }
              title={
                <div className="article-title" style={{ flexWrap: 'wrap', alignItems: 'flex-start', gap: '8px' }}>
                  {article.is_featured && (
                    <Tag color="gold" icon={<StarOutlined />}>
                      精选
                    </Tag>
                  )}
                  {article.title}
                  {article.is_read ? (
                    <Tag color="success" icon={<ReadOutlined />}>
                      已读
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ flexShrink: 0 }}>
                      未读
                    </Tag>
                  )}
                  <Text type="secondary" className="article-time-info" style={{ fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {dayjs(article.collected_at).fromNow()}
                  </Text>
                  {article.reading_time > 0 && (
                    <Tag color="processing" style={{ fontSize: '12px', flexShrink: 0 }}>
                      {article.reading_time}分钟
                    </Tag>
                  )}
                </div>
              }
              description={
                <div className="article-meta">
                  <Text type="secondary" className="article-summary">
                    {article.summary}
                  </Text>
                  <div className="article-info">
                    <Space size="small" wrap>
                      <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                        {article.site_name}
                      </Text>
                    </Space>
                    {article.tags && article.tags.length > 0 && (
                      <div className="article-tags" style={{ flexShrink: 0 }}>
                        {article.tags.slice(0, 3).map(tag => (
                          <Tag
                            key={tag.id}
                            color={tag.color || 'blue'}
                          >
                            {tag.name}
                          </Tag>
                        ))}
                        {article.tags.length > 3 && (
                          <Tag>+{article.tags.length - 3}</Tag>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    )
  )

  const quickActions = [
    {
      icon: <SearchOutlined />,
      title: '搜索收藏',
      description: '快速查找你的知识库',
      onClick: () => navigate('/search'),
      color: 'var(--brand)'
    },
   {
     icon: <PlusOutlined />,
     title: '添加收藏',
     description: '手动添加文章收藏',
     onClick: handleAddBookmark,
     color: 'var(--success)'
   },
    {
      icon: <ToolOutlined />,
      title: '添加工具',
      description: '粘贴文本，AI 自动识别拆解',
      onClick: () => setAddToolVisible(true),
      color: 'var(--info)'
    },
   {
     icon: <ToolOutlined />,
     title: '搜索工具',
     description: '独立检索结构化工具库',
     onClick: () => navigate('/tools'),
     color: 'var(--info)'
   },
    {
      icon: <TagOutlined />,
      title: '管理标签',
      description: '组织你的标签系统',
      onClick: () => navigate('/tags'),
      color: 'var(--brand-fg)'
    },
    {
      icon: <FolderOutlined />,
      title: '管理文件夹',
      description: '整理你的文件夹结构',
      onClick: () => navigate('/folders'),
      color: 'var(--muted)'
    }
  ]

  return (
    <Layout className="dashboard-layout">
      <Header className="dashboard-header">
        <div className="header-left">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="logo-icon">📚</span>
            <span className="logo-text">CrawlBox</span>
          </div>
        </div>
        
        <div className="header-right">
          <Space size="large">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddBookmarkVisible(true)}
            >
              添加收藏
            </Button>
            <Button
              icon={<SearchOutlined />}
              onClick={() => navigate('/search')}
            >
              搜索
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => navigate('/articles')}
            >
              文章管理
            </Button>
            <Button
              icon={<ToolOutlined />}
              onClick={() => navigate('/tools')}
            >
              工具库
            </Button>
            <Button
              icon={<TagOutlined />}
              onClick={() => navigate('/tags')}
            >
              标签
            </Button>
            <Button
              icon={<FolderOutlined />}
              onClick={() => navigate('/folders')}
            >
              文件夹
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => navigate('/settings')}
            >
              设置
            </Button>
            <Avatar 
              style={{ backgroundColor: 'var(--info)', cursor: 'pointer' }}
              onClick={() => navigate('/settings')}
            >
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Button 
              icon={<LogoutOutlined />} 
              onClick={handleLogout}
              danger
            >
              登出
            </Button>
          </Space>
        </div>
      </Header>

      <Content className="dashboard-content">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="加载中..." />
          </div>
        ) : (
          <>
           {/* 统计卡片 */}
            <Row gutter={[12, 12]} className="stats-section">
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="收藏文章"
                    value={stats?.article_count || 0}
                    prefix={<FileTextOutlined />}
                    valueStyle={{ color: 'var(--fg)' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="标签数量"
                    value={stats?.tag_count || 0}
                    prefix={<TagOutlined />}
                    valueStyle={{ color: 'var(--fg)' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="文件夹"
                    value={stats?.folder_count || 0}
                    prefix={<FolderOutlined />}
                    valueStyle={{ color: 'var(--brand-fg)' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="工具数量"
                    value={stats?.tool_count || 0}
                    prefix={<ToolOutlined />}
                    valueStyle={{ color: 'var(--info)' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="未读文章阅读时间"
                    value={stats?.unread_reading_time || 0}
                    suffix="分钟"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: 'var(--muted)' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                <Card className="stat-card">
                  <Statistic
                    title="今日收藏数"
                    value={stats?.today_collected_count || 0}
                    prefix={<CalendarOutlined />}
                    valueStyle={{ color: '#eb2f96' }}
                  />
                </Card>
              </Col>
            </Row>

           <Row gutter={[24, 24]}>
             {/* 快捷操作 */}
              <Col xs={24} lg={8}>
                <Card 
                  title="快捷操作" 
                  className="quick-actions-card"
                  extra={<SettingOutlined />}
                >
                  <div className="quick-actions-grid">
                    {quickActions.map((action, index) => (
                      <div 
                        key={index}
                        className="quick-action-item"
                        onClick={action.onClick}
                        style={{ borderLeftColor: action.color }}
                      >
                        <div className="action-icon" style={{ color: action.color }}>
                          {action.icon}
                        </div>
                        <div className="action-content">
                          <div className="action-title">{action.title}</div>
                          <div className="action-description">{action.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>

              {/* 最近收藏和历史查看 */}
              <Col xs={24} lg={8}>
                <Card
                  className="recent-articles-card"
                  extra={
                    <Button
                      type="link"
                      onClick={() => navigate('/search')}
                    >
                      查看全部
                    </Button>
                  }
                >
                  <Tabs
                    activeKey={activeTab}
                    onChange={async (key) => {
                      setActiveTab(key as 'recent' | 'history' | 'featured')
                      if (key === 'history') {
                        await loadHistoryArticles(true)
                      } else if (key === 'featured') {
                        await loadFeaturedArticles(true)
                      } else {
                        await reloadRecentArticles()
                      }
                    }}
                    items={[
                      {
                        key: 'recent',
                        label: '最近收藏',
                        children: renderArticleList(recentArticles, '还没有收藏任何文章')
                      },
                      {
                        key: 'history',
                        label: '历史查看',
                        children: renderArticleList(historyArticles, '还没有阅读过任何文章')
                      },
                      {
                        key: 'featured',
                        label: '精选列表',
                        children: renderArticleList(featuredArticles, '还没有精选任何文章')
                      }
                    ]}
                  />
                </Card>
              </Col>

              {/* 最近添加的工具 */}
              <Col xs={24} lg={8}>
                <Card
                  title={<Space><ToolOutlined /> 最近工具</Space>}
                  className="recent-tools-card"
                  extra={<Button type="link" onClick={() => navigate('/tools')}>查看全部</Button>}
                >
                  {recentTools.length === 0 ? (
                    <Empty description="还没有工具" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <List
                      dataSource={recentTools}
                      renderItem={(tool) => (
                        <List.Item
                          onClick={() => navigate('/tools')}
                          style={{ cursor: 'pointer' }}
                        >
                          <List.Item.Meta
                            avatar={<Avatar icon={<ToolOutlined />} style={{ backgroundColor: 'var(--info)' }} />}
                            title={tool.name}
                            description={<Text type="secondary" ellipsis>{tool.description || '暂无描述'}</Text>}
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </Card>
              </Col>
           </Row>
          </>
        )}
      </Content>

      {/* 添加收藏模态框 */}
      <AddBookmarkModal
        visible={addBookmarkVisible}
        onCancel={() => setAddBookmarkVisible(false)}
        onSuccess={() => {
          loadDashboardData() // 重新加载数据
        }}
      />

      {/* 添加工具模态框（AI 识别） */}
      <AddToolModal
        visible={addToolVisible}
        onCancel={() => setAddToolVisible(false)}
        onSuccess={() => {
          loadDashboardData()
        }}
      />
    </Layout>
  )
}

export default Dashboard
